/**
 * v2 (set2) calendar date-cell visuals — subtler fills, primary-tinted today, icon swatch legend.
 */
import type { TextStyle, ViewStyle } from 'react-native';
import { hexToRgba } from '../../shared/theme/colorUtils';
import type { ThemeColors } from '../../shared/theme/store';
import { getIconFromApiFormat, type CalendarDay } from '../../data/domains/calendar';
import type {
  CalendarDateCellIcon,
  CalendarDateCellVisual,
  ResolveCalendarDateCellVisualInput,
} from './calendarDateCellVisuals';

const CALENDAR_DATE_CELL_TUNING_SET2 = {
  booked: {
    borderWidth: 1,
    textWeight: '600' as const,
  },
  partial: {
    textWeight: '600' as const,
  },
  today: {
    borderOpacity: 0.22,
    borderWidth: 1,
    backgroundOpacity: 0.08,
    textWeight: '600' as const,
  },
  frozen: {
    borderWidth: 1,
  },
  paused: {
    borderWidth: 1,
  },
  doubleBox: {
    borderWidth: 1,
  },
  past: {
    cellOpacity: 0.82,
  },
  defaultText: {
    fontWeight: '500' as const,
  },
  icons: {
    todayActivePlan: 'driver' as const,
    frozen: 'freeze' as const,
    paused: 'lock' as const,
    doubleBox: 'gift' as const,
    dayOff: 'x' as const,
    booked: 'check' as const,
    partial: 'package' as const,
    selectMeal: 'plus' as const,
    feedback: 'star' as const,
    skipped: 'x' as const,
    onTheWay: 'driver' as const,
  },
} as const;

export interface CalendarLegendSwatchSet2 {
  iconName: string;
  iconColor: string;
  backgroundColor: string;
  borderColor: string;
}

export interface CalendarLegendSwatchesSet2 {
  mealSelected: CalendarLegendSwatchSet2;
  frozenDay: CalendarLegendSwatchSet2;
  pausedDay: CalendarLegendSwatchSet2;
  offDay: CalendarLegendSwatchSet2;
}

export function getCalendarLegendSwatchesSet2(theme: ThemeColors): CalendarLegendSwatchesSet2 {
  const icons = CALENDAR_DATE_CELL_TUNING_SET2.icons;
  return {
    mealSelected: {
      iconName: icons.booked,
      iconColor: theme.success,
      backgroundColor: theme.successUltra,
      borderColor: theme.success,
    },
    frozenDay: {
      iconName: icons.frozen,
      iconColor: theme.errorLight,
      backgroundColor: theme.errorUltra,
      borderColor: theme.errorLight,
    },
    pausedDay: {
      iconName: icons.paused,
      iconColor: theme.alert,
      backgroundColor: theme.alertUltra ?? theme.alertFade ?? theme.alert,
      borderColor: theme.alert,
    },
    offDay: {
      iconName: icons.dayOff,
      iconColor: theme.textLight,
      backgroundColor: theme.surfaceLight,
      borderColor: hexToRgba(theme.textLight, 0.2) ?? theme.borderLight,
    },
  };
}

type MealStateStyle = 'booked' | 'partial' | 'select';

function mealStateStyle(day: CalendarDay): MealStateStyle | null {
  if (day.state === 'Booked Meal') {
    return 'booked';
  }
  if (day.state === 'Partially Booked Meal') {
    return 'partial';
  }
  if (day.state === 'Select Meal') {
    return 'select';
  }
  return null;
}

function backgroundForMealState(state: MealStateStyle, theme: ThemeColors): ViewStyle {
  const tuning = CALENDAR_DATE_CELL_TUNING_SET2;
  if (state === 'booked') {
    return {
      borderWidth: tuning.booked.borderWidth / 2,
      borderStyle: 'solid',
      borderColor: theme.success,
      backgroundColor: theme.successUltra,
    };
  }
  if (state === 'partial') {
    return {
      borderWidth: tuning.booked.borderWidth,
      borderStyle: 'solid',
      borderColor: theme.warningUltra,
      backgroundColor: theme.warningUltra,
    };
  }
  return {
    borderWidth: tuning.booked.borderWidth,
    borderStyle: 'solid',
    borderColor: theme.primaryFade,
    backgroundColor: theme.primaryUltra,
  };
}

function textStyleForMealState(state: MealStateStyle, theme: ThemeColors): TextStyle {
  const weight =
    state === 'select' ? CALENDAR_DATE_CELL_TUNING_SET2.defaultText.fontWeight : CALENDAR_DATE_CELL_TUNING_SET2.booked.textWeight;
  const color = state === 'booked' ? theme.success : theme.text;
  return { color, fontWeight: weight };
}

function tintForMappedApiIcon(icon_raw: string, mapped: string, theme: ThemeColors): CalendarDateCellIcon {
  const low = icon_raw.toLowerCase();
  if (mapped === 'check') {
    if (low.includes('fa-duotone')) {
      return { name: 'package', color: theme.warning };
    }
    return { name: 'check', color: theme.success, size: 14 };
  }
  if (mapped === 'x' || low.includes('fa-ban') || low.includes('fa-times')) {
    return { name: mapped, color: theme.textLight };
  }
  if (mapped === 'driver' || low.includes('truck')) {
    return { name: mapped, color: theme.surface };
  }
  if (mapped === 'star' || low.includes('star')) {
    return { name: mapped, color: theme.warningLight };
  }
  if (mapped === 'plus' || low.includes('utensils')) {
    return { name: mapped, color: theme.primary };
  }
  if (mapped === 'package' || low.includes('fa-plate')) {
    return { name: mapped, color: theme.warning };
  }
  if (low.includes('pause')) {
    return { name: mapped, color: theme.errorFade, size: 14 };
  }
  if (low.includes('snow') || low.includes('snowflake')) {
    return { name: mapped, color: theme.errorFade, size: 14 };
  }
  return { name: mapped, color: theme.primary };
}

function resolveIcon(
  day: CalendarDay,
  theme: ThemeColors,
  has_active_plan: boolean,
  is_today_in_view: boolean,
  _meal_state: MealStateStyle | null,
): CalendarDateCellIcon | null {
  const icons = CALENDAR_DATE_CELL_TUNING_SET2.icons;

  if (is_today_in_view && has_active_plan && !day.isFrozen && !day.isPaused && !day.isDayOff) {
    return { name: icons.todayActivePlan, color: theme.primary, size: 16 };
  }
  if (day.isFrozen) {
    return { name: icons.frozen, color: theme.errorLight, size: 14 };
  }
  if (day.isPaused) {
    return { name: icons.paused, color: theme.alert, size: 14 };
  }
  if (day.icon) {
    const mapped = getIconFromApiFormat(day.icon);
    if (mapped) {
      return tintForMappedApiIcon(day.icon, mapped, theme);
    }
  }
  if (day.isDayOff) {
    return { name: icons.dayOff, color: theme.textLight };
  }
  if (day.state === 'Booked Meal') {
    return { name: icons.booked, color: theme.success, size: 14 };
  }
  if (day.state === 'Partially Booked Meal') {
    return { name: icons.partial, color: theme.warning, size: 14 };
  }
  if (day.isDoubleBox) {
    return { name: icons.doubleBox, color: theme.primary, size: 13 };
  }
  if (day.state === 'Select Meal' && day.isAvailable) {
    return { name: icons.selectMeal, color: theme.primary };
  }
  if (day.state === 'Feedback') {
    return { name: icons.feedback, color: theme.warningLight };
  }
  if (day.state === 'Skipped') {
    return { name: icons.skipped, color: theme.textLight };
  }
  if (day.state === 'On the way') {
    return { name: icons.onTheWay, color: theme.primary };
  }
  return null;
}

function resolveTextStyle(
  day: CalendarDay,
  theme: ThemeColors,
  meal_state: MealStateStyle | null,
  is_today_in_view: boolean,
  is_past: boolean,
): TextStyle | null {
  if (meal_state === 'booked' || meal_state === 'partial') {
    return textStyleForMealState(meal_state, theme);
  }
  if (day.isFrozen) {
    return { color: theme.error };
  }
  if (day.isPaused) {
    return { color: theme.alert };
  }
  if (day.isDoubleBox) {
    return { color: theme.primary };
  }
  if (day.isDayOff) {
    return { color: theme.textLight };
  }
  if (is_past) {
    return { color: theme.textLight };
  }
  if (is_today_in_view) {
    return { color: theme.primary, fontWeight: CALENDAR_DATE_CELL_TUNING_SET2.today.textWeight };
  }
  return null;
}

function resolveCellStyles(
  day: CalendarDay,
  theme: ThemeColors,
  meal_state: MealStateStyle | null,
  is_today_in_view: boolean,
  is_past: boolean,
): ViewStyle[] {
  const styles: ViewStyle[] = [];
  const tuning = CALENDAR_DATE_CELL_TUNING_SET2;

  if (day.isFrozen) {
    styles.push({
      borderWidth: tuning.frozen.borderWidth,
      borderStyle: 'solid',
      borderColor: theme.errorFade,
      backgroundColor: theme.errorUltra,
      opacity: 0.75,
      transform: [{ scale: 0.9 }],
    });
  }
  if (day.isPaused) {
    styles.push({
      borderWidth: tuning.paused.borderWidth,
      borderStyle: 'solid',
      borderColor: theme.alertFade ?? theme.alert,
      backgroundColor: theme.alertUltra ?? theme.alertFade,
      opacity: 0.85,
      transform: [{ scale: 0.92 }],
    });
  }
  if (day.isDoubleBox) {
    styles.push({
      borderWidth: tuning.doubleBox.borderWidth,
      borderStyle: 'solid',
      borderColor: theme.primaryFade ?? theme.primary,
      backgroundColor: theme.primaryUltra ?? theme.primaryFade,
    });
  }
  if (day.isDayOff) {
    styles.push({
      backgroundColor: theme.surfaceLight,
      borderWidth: tuning.frozen.borderWidth,
      borderStyle: 'solid',
      borderColor: theme.borderLight,
      opacity: 0.7,
      borderRadius: 999,
    });
  }
  if (meal_state) {
    styles.push(backgroundForMealState(meal_state, theme));
  }
  if (is_past) {
    styles.push({ opacity: tuning.past.cellOpacity });
  }
  if (is_today_in_view) {
    styles.push({
      borderWidth: tuning.today.borderWidth,
      borderStyle: 'solid',
      borderColor: hexToRgba(theme.primary, tuning.today.borderOpacity) ?? theme.primary,
      backgroundColor: hexToRgba(theme.primary, tuning.today.backgroundOpacity) ?? theme.primaryFade,
    });
  }
  return styles;
}

/** Resolve v2 calendar cell visuals (no looping animations). */
export function resolveCalendarDateCellVisualSet2(input: ResolveCalendarDateCellVisualInput): CalendarDateCellVisual {
  const { day, theme, is_today_in_view, is_past, has_active_plan } = input;
  const meal_state = mealStateStyle(day);

  return {
    cellStyles: resolveCellStyles(day, theme, meal_state, is_today_in_view, is_past),
    textStyle: resolveTextStyle(day, theme, meal_state, is_today_in_view, is_past),
    icon: resolveIcon(day, theme, has_active_plan, is_today_in_view, meal_state),
    animation: null,
  };
}
